import React, { useState, useEffect, useCallback } from "react";
import AdminLayout from "../components/admin/AdminLayout";
import api from "../utils/api";
import {
  Search, UserCheck, UserX, Link2, Unlink,
  Star, Loader2, RefreshCw, ChevronDown, Shield,
  Users, User, Music, X, ShieldCheck, ShieldOff, Disc3,
  Pencil, Check, Image as ImageIcon
} from "lucide-react";

/* ── Stats Card ── */
function StatCard({ label, value, color, icon: Icon }) {
  const colors = {
    indigo: "from-indigo-500/15 to-indigo-600/5 border-indigo-200/40 text-indigo-600",
    rose:   "from-rose-500/15 to-rose-600/5 border-rose-200/40 text-rose-600",
    amber:  "from-amber-500/15 to-amber-600/5 border-amber-200/40 text-amber-600",
    zinc:   "from-zinc-200/60 to-zinc-100/40 border-zinc-300/60 text-zinc-500",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-2xl px-5 py-4 flex items-center gap-4`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/60 shadow-sm flex-shrink-0">
        <Icon size={18} className={colors[color].split(" ").pop()} />
      </div>
      <div>
        <p className="text-2xl font-black text-zinc-900">{value}</p>
        <p className="text-zinc-500 text-xs font-medium">{label}</p>
      </div>
    </div>
  );
}

/* ── Multi-Badge (Rolle + Supporter + Artist + Label) ── */
function UserBadges({ user }) {
  const badges = [];
  if (user.role === "developer") {
    badges.push(<span key="dev" className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-200 text-xs font-bold rounded-md"><Shield size={9} /> Admin</span>);
    badges.push(<span key="founder" className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold rounded-md"><Shield size={9} /> Gründer</span>);
  } else if (user.role === "admin") {
    badges.push(<span key="admin" className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold rounded-md"><Shield size={9} /> Team</span>);
  }
  if (user.is_supporter) {
    badges.push(<span key="sup" className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 text-xs font-bold rounded-md"><Star size={9} /> Supporter</span>);
  }
  if (user.artist_id) {
    badges.push(<span key="art" className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-bold rounded-md"><Music size={9} /> Artist</span>);
  }
  if (user.is_label_manager) {
    badges.push(<span key="label" className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-md" style={{ background: "rgba(16,185,129,0.1)", color: "#059669", border: "1px solid rgba(16,185,129,0.25)" }}><Disc3 size={9} /> Label</span>);
  }
  if (badges.length === 0) {
    badges.push(<span key="user" className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 text-zinc-600 border border-zinc-200 text-xs font-semibold rounded-md"><User size={9} /> User</span>);
  }
  return <div className="flex flex-wrap gap-1">{badges}</div>;
}

/* ── Artist Search Dropdown ── */
function ArtistSearchDropdown({ onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/public-users/artists-list?search=${encodeURIComponent(q)}`);
        setResults(r.data || []);
        setOpen(true);
      } catch {}
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-3 py-2 shadow-sm">
        <Search size={13} className="text-zinc-400 flex-shrink-0" />
        <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Artist suchen…" className="text-sm text-zinc-900 flex-1 outline-none bg-transparent placeholder-zinc-400" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-white border border-zinc-200 rounded-xl shadow-xl max-h-44 overflow-y-auto">
          {results.map(a => (
            <button key={a.id} onMouseDown={() => { onSelect(a); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 text-sm text-zinc-800 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0">
              {a.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Label Search Dropdown ── */
function LabelSearchDropdown({ onSelect }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/labels/?search=${encodeURIComponent(q)}&limit=8`);
        setResults(r.data?.labels || []);
        setOpen(true);
      } catch {}
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative">
      <div className="flex items-center gap-2 bg-white border border-zinc-200 rounded-xl px-3 py-2 shadow-sm">
        <Disc3 size={13} className="text-zinc-400 flex-shrink-0" />
        <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Label suchen…" className="text-sm text-zinc-900 flex-1 outline-none bg-transparent placeholder-zinc-400" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 z-50 bg-white border border-zinc-200 rounded-xl shadow-xl max-h-44 overflow-y-auto">
          {results.map(l => (
            <button key={l.id} onMouseDown={() => { onSelect(l); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 text-sm text-zinc-800 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0">
              <span className="font-medium">{l.name}</span>
              <span className="text-xs text-zinc-400 ml-2">{l.release_count} Releases</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── User Row ── */
function UserRow({ user, onToggleSupporter, onLinkArtist, onAddArtist, onRemoveArtist, onSetRole, onAssignLabel, onRemoveLabel, onEditProfile, isEven }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [labelSaving, setLabelSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ username: user.username || "", avatar_url: user.avatar_url || "" });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [pendingArtist, setPendingArtist] = useState(null);
  const [pendingLabel, setPendingLabel] = useState(null);
  const [stripeInfo, setStripeInfo] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const isPublic = user.account_type === "public";

  // Stripe-Daten laden wenn expanded + is_supporter
  useEffect(() => {
    if (!expanded || !user.is_supporter || stripeInfo) return;
    const auth0_id = user.auth0_id || user.id;
    if (!auth0_id) return;
    setStripeLoading(true);
    api.get(`/subscriptions/admin/user-stripe/${encodeURIComponent(auth0_id)}`)
      .then(r => setStripeInfo(r.data))
      .catch(() => setStripeInfo({}))
      .finally(() => setStripeLoading(false));
  }, [expanded, user.is_supporter]); // eslint-disable-line

  const handleSupporterToggle = async () => {
    setSaving(true);
    const userId = user.auth0_id || user.id;
    await onToggleSupporter(userId, !user.is_supporter);
    setSaving(false);
  };

  const handleAddArtist = async (artistId) => {
    setSaving(true);
    await onAddArtist(user.auth0_id, artistId);
    setSaving(false);
    setPendingArtist(null);
  };

  const handleRemoveArtist = async (artistId) => {
    setSaving(true);
    await onRemoveArtist(user.auth0_id, artistId);
    setSaving(false);
  };

  const handleRoleToggle = async () => {
    setRoleSaving(true);
    const newRole = user.role === "admin" ? "user" : "admin";
    await onSetRole(user.auth0_id, newRole);
    setRoleSaving(false);
  };

  const handleAssignLabel = async (label) => {
    setLabelSaving(true);
    await onAssignLabel(user.auth0_id, label);
    setLabelSaving(false);
    setPendingLabel(null);
  };

  const handleRemoveLabel = async (labelId) => {
    setLabelSaving(true);
    await onRemoveLabel(user.auth0_id, labelId);
    setLabelSaving(false);
  };

  const handleSaveProfile = async () => {
    setEditSaving(true); setEditError("");
    const userId = user.auth0_id || user.id;
    try {
      await onEditProfile(userId, editForm);
      setEditMode(false);
    } catch (e) {
      setEditError(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setEditSaving(false); }
  };

  const rowBg = isEven ? "bg-white" : "bg-zinc-50/60";

  return (
    <>
      <tr className={`${rowBg} border-b border-zinc-100 hover:bg-blue-50/30 transition-colors group`} data-testid={`user-row-${user.id}`}>
        {/* Avatar + Name */}
        <td className="px-5 py-3">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex-shrink-0 overflow-hidden ring-1 ${
              user.role === "developer" ? "ring-yellow-300" :
              user.role === "admin" ? "ring-indigo-300" :
              user.is_supporter ? "ring-rose-300" :
              user.artist_id ? "ring-blue-300" : "ring-zinc-200"
            }`}>
              {user.avatar_url
                ? <img src={user.avatar_url} alt={user.username} className="w-full h-full object-cover" />
                : <div className={`w-full h-full flex items-center justify-center text-xs font-bold ${user.account_type === "admin" ? "bg-indigo-100 text-indigo-600" : "bg-zinc-200 text-zinc-600"}`}>
                    {(user.username || user.email || "?")[0]?.toUpperCase()}
                  </div>
              }
            </div>
            <div className="min-w-0">
              <p className="text-zinc-900 font-semibold text-sm truncate max-w-[110px] sm:max-w-[160px]">{user.username || <span className="text-zinc-400 italic">kein Name</span>}</p>
              <p className="text-zinc-500 text-xs truncate max-w-[110px] sm:max-w-[160px]">{user.email}</p>
            </div>
          </div>
        </td>

        {/* Type */}
        <td className="px-4 py-3">
          <UserBadges user={user} />
        </td>

        {/* Artist(s) */}
        <td className="px-4 py-3 hidden sm:table-cell">
          {(user.artist_ids?.length > 0)
            ? <div className="flex items-center gap-1 flex-wrap">
                {user.artist_ids.map(aid => (
                  <span key={aid} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs font-semibold rounded-md whitespace-nowrap">
                    <Link2 size={9} /> {user.artist_names?.[aid] || aid.slice(-5)}
                  </span>
                ))}
              </div>
            : user.artist_name
              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 text-xs font-semibold rounded-md">
                  <Link2 size={9} /> {user.artist_name}
                </span>
              : <span className="text-zinc-300 text-xs">—</span>
          }
        </td>

        {/* Registered */}
        <td className="px-4 py-3 text-zinc-500 text-xs whitespace-nowrap hidden md:table-cell">
          {user.created_at ? new Date(user.created_at).toLocaleDateString("de-DE") : "—"}
        </td>

        {/* Actions */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-1.5 justify-end">
            {/* Role toggle: Admin ↔ User (nur für Public-Auth0-User, nicht Developer) */}
            {isPublic && user.role !== "developer" && (
              <button
                onClick={handleRoleToggle}
                disabled={roleSaving}
                data-testid={`toggle-role-${user.auth0_id}`}
                title={user.role === "admin" ? "Admin-Rolle entfernen" : "Als Admin setzen"}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                  user.role === "admin"
                    ? "bg-indigo-50 border-indigo-200 text-indigo-600 hover:bg-indigo-100"
                    : "bg-zinc-50 border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-indigo-600"
                }`}
              >
                {roleSaving ? <Loader2 size={10} className="animate-spin" /> : user.role === "admin" ? <ShieldOff size={10} /> : <ShieldCheck size={10} />}
                <span className="hidden sm:inline">{user.role === "admin" ? "Admin" : "Admin?"}</span>
              </button>
            )}
            {/* Supporter toggle for ALL user types */}
            <button
                onClick={handleSupporterToggle}
                disabled={saving}
                data-testid={`toggle-supporter-${user.auth0_id || user.id}`}
                title={user.is_supporter ? "Supporter entfernen" : "Als Supporter setzen"}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                  user.is_supporter
                    ? "bg-rose-50 border-rose-200 text-rose-600 hover:bg-rose-100"
                    : "bg-zinc-50 border-zinc-200 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
                }`}
              >
                {saving ? <Loader2 size={10} className="animate-spin" /> : user.is_supporter ? <UserX size={10} /> : <UserCheck size={10} />}
                <span className="hidden sm:inline">{user.is_supporter ? "Supporter" : "Setzen"}</span>
              </button>
            {isPublic && (
              <button
                onClick={() => { setExpanded(!expanded); if (editMode) setEditMode(false); }}
                data-testid={`expand-user-${user.id}`}
                className={`p-1.5 rounded-lg border text-xs transition-all ${expanded ? "bg-zinc-800 border-zinc-700 text-white" : "bg-zinc-50 border-zinc-200 text-zinc-400 hover:border-zinc-300 hover:text-zinc-700"}`}
              >
                <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
            )}
            {/* Profil-Editieren Button */}
            <button
              onClick={() => { setEditMode(!editMode); setExpanded(true); setEditError(""); setEditForm({ username: user.username || "", avatar_url: user.avatar_url || "" }); }}
              data-testid={`edit-profile-${user.auth0_id || user.id}`}
              title="Profil bearbeiten (Name / Bild)"
              className={`p-1.5 rounded-lg border text-xs transition-all ${editMode ? "bg-amber-50 border-amber-200 text-amber-600" : "bg-zinc-50 border-zinc-200 text-zinc-400 hover:border-zinc-300 hover:text-zinc-700"}`}
            >
              <Pencil size={12} />
            </button>
          </div>
        </td>
      </tr>

      {/* Expanded: Profil-Edit + Artist Link + Stripe Info */}
      {expanded && (
        <tr className="bg-blue-50/40 border-b border-zinc-100">
          <td colSpan={5} className="px-5 py-4">
            <div className="space-y-4 max-w-2xl">

              {/* ── Profil bearbeiten ── */}
              {editMode && (
                <div className="bg-amber-50/60 border border-amber-200/60 rounded-xl p-4">
                  <p className="text-zinc-800 text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Pencil size={11} className="text-amber-600" /> Profil bearbeiten
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-zinc-600 text-xs font-semibold block mb-1">Anzeigename</label>
                      <input
                        value={editForm.username}
                        onChange={e => setEditForm(f => ({ ...f, username: e.target.value }))}
                        placeholder="Benutzername"
                        maxLength={50}
                        className="w-full bg-white border border-zinc-200 rounded-lg px-3 py-2 text-sm text-zinc-900 outline-none focus:border-amber-400 transition"
                        data-testid={`edit-username-${user.auth0_id || user.id}`}
                      />
                    </div>
                    <div>
                      <label className="text-zinc-600 text-xs font-semibold block mb-1 flex items-center gap-1.5">
                        <ImageIcon size={11} /> Profilbild URL
                      </label>
                      <div className="flex items-center gap-2">
                        {editForm.avatar_url && (
                          <img src={editForm.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover bg-zinc-200 flex-shrink-0" onError={e => e.target.style.display='none'} />
                        )}
                        <input
                          value={editForm.avatar_url}
                          onChange={e => setEditForm(f => ({ ...f, avatar_url: e.target.value }))}
                          placeholder="https://..."
                          className="flex-1 bg-white border border-zinc-200 rounded-lg px-3 py-2 text-sm text-zinc-900 outline-none focus:border-amber-400 transition"
                          data-testid={`edit-avatar-${user.auth0_id || user.id}`}
                        />
                      </div>
                    </div>
                  </div>
                  {editError && <p className="text-red-500 text-xs mb-2">{editError}</p>}
                  <div className="flex items-center gap-2">
                    <button onClick={handleSaveProfile} disabled={editSaving}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition disabled:opacity-50">
                      {editSaving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                      Speichern
                    </button>
                    <button onClick={() => { setEditMode(false); setEditError(""); }}
                      className="flex items-center gap-1 px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-600 text-xs font-semibold rounded-lg border border-zinc-200 transition">
                      <X size={11} /> Abbrechen
                    </button>
                  </div>
                </div>
              )}

              {/* ── Stripe Abo-Info (nur bei Supportern) ── */}
              {user.is_supporter && (
                <div>
                  <p className="text-zinc-700 text-xs font-bold uppercase tracking-wider mb-2">Stripe / Abonnement</p>
                  {stripeLoading ? (
                    <div className="flex items-center gap-2 text-zinc-400 text-xs py-2"><Loader2 size={12} className="animate-spin" /> Lade Stripe-Daten…</div>
                  ) : stripeInfo ? (
                    <div className="bg-white border border-zinc-200 rounded-xl p-3 space-y-2.5 text-xs">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5">
                        <div>
                          <span className="text-zinc-400">Supporter seit</span>
                          <p className="text-zinc-800 font-semibold">
                            {stripeInfo.supporter_since
                              ? new Date(stripeInfo.supporter_since).toLocaleDateString("de-DE")
                              : stripeInfo.subscription?.current_period_start
                                ? new Date(stripeInfo.subscription.current_period_start).toLocaleDateString("de-DE")
                                : "—"}
                          </p>
                        </div>
                        <div>
                          <span className="text-zinc-400">Plan</span>
                          <p className="text-zinc-800 font-semibold capitalize">
                            {stripeInfo.supporter_plan
                              || stripeInfo.subscription?.interval
                              || (stripeInfo.given_supporter_by && !stripeInfo.stripe_customer_id ? "Geschenkt" : "—")}
                          </p>
                        </div>
                        {stripeInfo.subscription && (
                          <>
                            <div>
                              <span className="text-zinc-400">Status</span>
                              <p className={`font-bold ${stripeInfo.subscription.status === "active" ? "text-emerald-600" : stripeInfo.subscription.status === "past_due" ? "text-amber-600" : "text-red-500"}`}>
                                {stripeInfo.subscription.status}
                                {stripeInfo.subscription.cancel_at_period_end && " (Kündigung)"}
                              </p>
                            </div>
                            <div>
                              <span className="text-zinc-400">{stripeInfo.subscription.cancel_at_period_end ? "Läuft aus am" : "Nächste Zahlung"}</span>
                              <p className="text-zinc-800 font-semibold">
                                {stripeInfo.subscription.current_period_end ? new Date(stripeInfo.subscription.current_period_end).toLocaleDateString("de-DE") : "—"}
                              </p>
                            </div>
                          </>
                        )}
                        {stripeInfo.stripe_customer_id && (
                          <div>
                            <span className="text-zinc-400">Customer ID</span>
                            <p className="text-zinc-600 font-mono text-[10px]">{stripeInfo.stripe_customer_id}</p>
                          </div>
                        )}
                        {!stripeInfo.stripe_customer_id && (
                          <div>
                            <span className="text-zinc-400">Stripe</span>
                            <p className="text-amber-600 font-semibold">Kein Stripe-Konto</p>
                          </div>
                        )}
                      </div>
                      {/* Letzte Zahlungen */}
                      {stripeInfo.invoices?.length > 0 && (
                        <div className="pt-2 border-t border-zinc-100">
                          <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1.5">Letzte Zahlungen</p>
                          <div className="space-y-1">
                            {stripeInfo.invoices.slice(0, 5).map(inv => (
                              <div key={inv.id} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className={`w-1.5 h-1.5 rounded-full ${inv.paid ? "bg-emerald-500" : "bg-amber-500"}`} />
                                  <span className="text-zinc-500">{inv.created ? new Date(inv.created).toLocaleDateString("de-DE") : "—"}</span>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-zinc-800 font-semibold">{inv.amount_paid?.toFixed(2)} {inv.currency}</span>
                                  {inv.hosted_invoice_url && (
                                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-zinc-700 text-[10px]">Beleg ↗</a>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
              {/* ── Artists verknüpfen ── */}
              <div className="space-y-3">
                <p className="text-zinc-700 text-xs font-bold uppercase tracking-wider">Artists verknüpfen</p>

              {/* Bereits verknüpfte Artists */}
              {(user.artist_ids || []).length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {(user.artist_ids || []).map(aid => (
                    <span key={aid} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-zinc-900 text-white text-xs font-semibold rounded-xl">
                      {user.artist_names?.[aid] || aid.slice(-6)}
                      <button onClick={() => handleRemoveArtist(aid)} disabled={saving}
                        className="text-zinc-500 hover:text-red-400 transition-colors">
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Artist hinzufügen */}
              <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
                <div className="flex-1">
                  <ArtistSearchDropdown onSelect={(a) => setPendingArtist(a)} />
                </div>
                <div className="flex gap-2">
                  {pendingArtist && (
                    <button
                      onClick={() => handleAddArtist(pendingArtist.id)}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-bold rounded-xl border border-zinc-700 transition-colors"
                    >
                      {saving ? <Loader2 size={10} className="animate-spin" /> : <Link2 size={10} />}
                      {pendingArtist.name} hinzufügen
                    </button>
                  )}
                  <button onClick={() => setExpanded(false)} className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors">
                    <X size={13} />
                  </button>
                </div>
              </div>

              </div>

              {/* ── Labels zuweisen ── */}
              {isPublic && (
                <div className="space-y-3 pt-3 border-t border-zinc-200">
                  <p className="text-zinc-700 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                    <Disc3 size={11} style={{ color: "#10b981" }} /> Labels zuweisen
                  </p>

                  {/* Vorhandene Labels als Tags */}
                  {((user.label_ids?.length > 0) || user.label_id) && (
                    <div className="flex flex-wrap gap-2">
                      {(user.label_ids?.length > 0 ? user.label_ids : (user.label_id ? [user.label_id] : [])).map(lid => (
                        <span key={lid} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-xl"
                          style={{ background: "rgba(16,185,129,0.1)", color: "#059669", border: "1px solid rgba(16,185,129,0.25)" }}>
                          <Disc3 size={10} />
                          {(user.label_names?.[lid]) || user.label_name || lid.slice(-6)}
                          <button
                            onClick={() => handleRemoveLabel(lid)}
                            disabled={labelSaving}
                            title="Label entfernen"
                            className="ml-0.5 hover:text-red-600 transition-colors disabled:opacity-50"
                          >
                            {labelSaving ? <Loader2 size={8} className="animate-spin" /> : <X size={8} />}
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Label hinzufügen */}
                  <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-3">
                    <div className="flex-1">
                      <LabelSearchDropdown onSelect={(l) => setPendingLabel(l)} />
                    </div>
                    <div className="flex gap-2">
                      {pendingLabel && (
                        <button onClick={() => handleAssignLabel(pendingLabel)} disabled={labelSaving}
                          className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl border transition-colors"
                          style={{ background: "rgba(16,185,129,0.1)", color: "#059669", borderColor: "rgba(16,185,129,0.3)" }}>
                          {labelSaving ? <Loader2 size={10} className="animate-spin" /> : <Disc3 size={10} />}
                          {pendingLabel.name} hinzufügen
                        </button>
                      )}
                      <button onClick={() => setExpanded(false)} className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors">
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Main Page ── */
export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");

  const load = useCallback(async (q = "", type = "all") => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/public-users/?search=${encodeURIComponent(q)}&user_type=${type}&limit=200`);
      setUsers(res.data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search, filterType), 380);
    return () => clearTimeout(t);
  }, [search, filterType, load]);

  const handleToggleSupporter = async (userId, is_supporter) => {
    await api.put(`/admin/public-users/${encodeURIComponent(userId)}/supporter`, { is_supporter });
    setUsers(prev => prev.map(u => {
      const uid = u.auth0_id || u.id;
      return uid === userId ? { ...u, is_supporter } : u;
    }));
  };

  const handleLinkArtist = async (auth0_id, artist_id) => {
    await api.put(`/admin/public-users/${encodeURIComponent(auth0_id)}/artist`, { artist_id });
    load(search, filterType);
  };

  const handleAddArtist = async (auth0_id, artist_id) => {
    try {
      const res = await api.post(`/admin/public-users/${encodeURIComponent(auth0_id)}/artists/add`, { artist_id });
      // Lokalen State updaten
      setUsers(prev => prev.map(u => {
        if (u.auth0_id !== auth0_id) return u;
        const ids = [...new Set([...(u.artist_ids || []), artist_id])];
        const names = { ...(u.artist_names || {}), [artist_id]: res.data?.artist_name };
        return { ...u, artist_ids: ids, artist_names: names, artist_id: ids[0] };
      }));
    } catch (e) {
      console.error("Artist hinzufügen fehlgeschlagen", e);
    }
  };

  const handleRemoveArtist = async (auth0_id, artist_id) => {
    try {
      await api.delete(`/admin/public-users/${encodeURIComponent(auth0_id)}/artists/remove`, { data: { artist_id } });
      setUsers(prev => prev.map(u => {
        if (u.auth0_id !== auth0_id) return u;
        const ids = (u.artist_ids || []).filter(id => id !== artist_id);
        return { ...u, artist_ids: ids, artist_id: ids[0] || null };
      }));
    } catch (e) {
      console.error("Artist entfernen fehlgeschlagen", e);
    }
  };

  const handleSetRole = async (auth0_id, role) => {
    try {
      await api.put(`/admin/public-users/${encodeURIComponent(auth0_id)}/role`, { role });
      setUsers(prev => prev.map(u => u.auth0_id === auth0_id ? { ...u, role } : u));
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler beim Ändern der Rolle");
    }
  };

  const handleAssignLabel = async (auth0_id, label) => {
    try {
      await api.put(`/admin/public-users/${encodeURIComponent(auth0_id)}/label`, {
        label_id: label.id, label_name: label.name,
      });
      setUsers(prev => prev.map(u => {
        if (u.auth0_id !== auth0_id) return u;
        const newLabelIds = [...new Set([...(u.label_ids || (u.label_id ? [u.label_id] : [])), label.id])];
        const newLabelNames = { ...(u.label_names || {}), [label.id]: label.name };
        return {
          ...u,
          is_label_manager: true,
          label_id: u.label_id || label.id,
          label_name: u.label_name || label.name,
          label_ids: newLabelIds,
          label_names: newLabelNames,
        };
      }));
    } catch (e) {
      alert(e.response?.data?.detail || "Fehler beim Zuweisen");
    }
  };

  const handleRemoveLabel = async (auth0_id, label_id) => {
    try {
      await api.delete(`/admin/public-users/${encodeURIComponent(auth0_id)}/label/${label_id}`);
      setUsers(prev => prev.map(u => {
        if (u.auth0_id !== auth0_id) return u;
        const newLabelIds = (u.label_ids || (u.label_id ? [u.label_id] : [])).filter(id => id !== label_id);
        const newLabelNames = { ...(u.label_names || {}) };
        delete newLabelNames[label_id];
        return {
          ...u,
          label_ids: newLabelIds,
          label_names: newLabelNames,
          is_label_manager: newLabelIds.length > 0,
          label_id: newLabelIds[0] || null,
          label_name: newLabelIds[0] ? (newLabelNames[newLabelIds[0]] || null) : null,
        };
      }));
    } catch (e) {
      alert(e.response?.data?.detail || "Fehler beim Entfernen");
    }
  };

  const handleEditProfile = async (userId, form) => {
    const payload = {};
    if (form.username !== undefined) payload.username = form.username;
    if (form.avatar_url !== undefined) payload.avatar_url = form.avatar_url;
    await api.patch(`/admin/public-users/${encodeURIComponent(userId)}/profile`, payload);
    // Lokalen State sofort aktualisieren
    setUsers(prev => prev.map(u => {
      const uid = u.auth0_id || u.id;
      if (uid !== userId) return u;
      return {
        ...u,
        ...(payload.username !== undefined ? { username: payload.username } : {}),
        ...(payload.avatar_url !== undefined ? { avatar_url: payload.avatar_url || null } : {}),
      };
    }));
  };

  const total      = users.length;
  const withArtist = users.filter(u => (u.artist_ids?.length > 0) || u.artist_id).length;
  const supporters = users.filter(u => u.is_supporter).length;
  const publicN    = users.filter(u => u.account_type === "public").length;

  const FILTERS = [
    { value: "all",    label: "Alle" },
    { value: "admin",  label: "Admins" },
    { value: "public", label: "Public User" },
  ];

  return (
    <AdminLayout>
      {/* Light page background */}
      <div className="min-h-full bg-zinc-50">
        {/* ── Page Header ── */}
        <div className="bg-white border-b border-zinc-200 px-4 sm:px-8 py-4 sm:py-6">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-xl font-black text-zinc-900 mb-0.5">Benutzerverwaltung</h1>
            <p className="text-zinc-500 text-sm">Admins, Public-User, Supporter & Artist-Verknüpfungen</p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">
          {/* ── Stats ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <StatCard label="Alle Benutzer" value={total} color="zinc" icon={Users} />
            <StatCard label="Public User"   value={publicN} color="zinc" icon={User} />
            <StatCard label="Supporter"     value={supporters} color="rose" icon={Star} />
            <StatCard label="Mit Artist"    value={withArtist} color="indigo" icon={Shield} />
          </div>

          {/* ── Toolbar ── */}
          <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm p-3 sm:p-4 flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Search */}
            <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-xl px-3 sm:px-4 py-2.5 flex-1 min-w-[160px]">
              <Search size={14} className="text-zinc-400 flex-shrink-0" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Name oder E-Mail suchen…"
                className="bg-transparent text-sm text-zinc-900 flex-1 outline-none placeholder-zinc-400 min-w-0"
                data-testid="user-search-input"
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-zinc-400 hover:text-zinc-600 transition-colors">
                  <X size={13} />
                </button>
              )}
            </div>

            {/* Type Filter */}
            <div className="flex items-center gap-1 p-1 bg-zinc-100 rounded-xl border border-zinc-200 flex-wrap">
              {FILTERS.map(f => (
                <button
                  key={f.value}
                  onClick={() => setFilterType(f.value)}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    filterType === f.value
                      ? "bg-white text-zinc-900 shadow-sm border border-zinc-200"
                      : "text-zinc-500 hover:text-zinc-700"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => load(search, filterType)}
              className="p-2.5 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 rounded-xl text-zinc-500 hover:text-zinc-700 transition-colors"
              title="Aktualisieren"
            >
              <RefreshCw size={14} />
            </button>
          </div>

          {/* ── Table ── */}
          <div className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={22} className="animate-spin text-zinc-400" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-16 text-zinc-400 text-sm">
                <Users size={32} className="mx-auto mb-3 opacity-30" />
                Keine Benutzer gefunden
              </div>
            ) : (
              <table className="w-full" data-testid="users-table">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-5 py-3 text-zinc-600 text-xs font-bold uppercase tracking-wide whitespace-nowrap">Benutzer</th>
                    <th className="text-left px-4 py-3 text-zinc-600 text-xs font-bold uppercase tracking-wide whitespace-nowrap">Typ / Rollen</th>
                    <th className="text-left px-4 py-3 text-zinc-600 text-xs font-bold uppercase tracking-wide whitespace-nowrap hidden sm:table-cell">Artist</th>
                    <th className="text-left px-4 py-3 text-zinc-600 text-xs font-bold uppercase tracking-wide whitespace-nowrap hidden md:table-cell">Registriert</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, i) => (
                    <UserRow
                      key={u.id}
                      user={u}
                      isEven={i % 2 === 0}
                      onToggleSupporter={handleToggleSupporter}
                      onLinkArtist={handleLinkArtist}
                      onAddArtist={handleAddArtist}
                      onRemoveArtist={handleRemoveArtist}
                      onSetRole={handleSetRole}
                      onAssignLabel={handleAssignLabel}
                      onRemoveLabel={handleRemoveLabel}
                      onEditProfile={handleEditProfile}
                    />
                  ))}
                </tbody>
              </table>
            )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
